import { useState } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { ProcessType, ProcessItemRequest, Item } from '../../../types/response'
import { getItems } from '../../../services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Button } from 'vnetwork-ui'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { Search, Plus, Package, Server, Cpu } from 'lucide-react'
import { cn } from '@/lib/utils'

type ItemTypeFilter = 'all' | 'equipment' | 'component'

interface ItemSelectorProps {
    processType: ProcessType
    sourceLocationId?: string // For transfers: filter items at source location
    onSelect: (item: ProcessItemRequest) => void
}

const ItemSelector = ({
    processType,
    sourceLocationId,
    onSelect,
}: ItemSelectorProps) => {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [itemTypeFilter, setItemTypeFilter] = useState<ItemTypeFilter>('all')
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())

    // For transfers, filter by source location
    const isTransfer = processType === 'internal_transfer'
    const locationFilter = isTransfer ? sourceLocationId : undefined

    // Build query params
    const queryParams: Record<string, any> = {
        search,
        page_size: 50,
    }
    if (itemTypeFilter !== 'all') {
        queryParams.item_type = itemTypeFilter
    }
    if (locationFilter) {
        queryParams.location_id = locationFilter
    }

    // Fetch items with filters
    const { data: itemsData, isLoading } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.WAREHOUSE_ITEMS,
                search,
                itemTypeFilter,
                locationFilter,
            ],
            queryFn: () => getItems(queryParams),
            enabled: open,
        },
        { showLoading: false, showError: true }
    )

    const items = itemsData?.results || []

    const handleToggleItem = (item: Item) => {
        const newSelected = new Set(selectedItems)
        if (newSelected.has(item.id)) {
            newSelected.delete(item.id)
        } else {
            newSelected.add(item.id)
        }
        setSelectedItems(newSelected)
    }

    const handleAddSelected = () => {
        items
            .filter((item) => selectedItems.has(item.id))
            .forEach((item) => {
                onSelect({
                    item_id: item.id,
                    quantity: 1,
                    condition: item.condition,
                    notes: '',
                })
            })

        setSelectedItems(new Set())
        setSearch('')
        setOpen(false)
    }

    const getStatusBadgeVariant = (status: string) => {
        switch (status.toLowerCase()) {
            case 'available':
            case 'in_warehouse':
                return 'default'
            case 'in_use':
            case 'in_datacenter':
                return 'secondary'
            case 'damaged':
                return 'destructive'
            default:
                return 'outline'
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outlined"
                    type="button"
                    data-tour="warehouse-add-items-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Items
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Select Items</DialogTitle>
                </DialogHeader>

                {/* Item Type Tabs */}
                <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
                    <button
                        type="button"
                        onClick={() => setItemTypeFilter('all')}
                        className={cn(
                            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                            itemTypeFilter === 'all'
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        All
                    </button>
                    <button
                        type="button"
                        onClick={() => setItemTypeFilter('equipment')}
                        className={cn(
                            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5',
                            itemTypeFilter === 'equipment'
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Server className="w-3.5 h-3.5" />
                        Equipment
                    </button>
                    <button
                        type="button"
                        onClick={() => setItemTypeFilter('component')}
                        className={cn(
                            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5',
                            itemTypeFilter === 'component'
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Cpu className="w-3.5 h-3.5" />
                        Component
                    </button>
                </div>

                {/* Search + Location Info */}
                <div className="space-y-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search items by name, service tag, serial number..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    {isTransfer && sourceLocationId && (
                        <p className="text-xs text-muted-foreground">
                            Showing items at source location only
                        </p>
                    )}
                    {isTransfer && !sourceLocationId && (
                        <p className="text-xs text-amber-600">
                            Select a source location to filter items
                        </p>
                    )}
                </div>

                {/* Items List */}
                <div className="flex-1 overflow-auto min-h-[300px] border rounded-lg">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-muted-foreground">
                                Loading items...
                            </div>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                            <Package className="w-12 h-12 mb-2 opacity-50" />
                            <div>No items found</div>
                            {search && (
                                <div className="text-sm mt-1">
                                    Try a different search term
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="divide-y">
                            {items.map((item) => {
                                const isSelected = selectedItems.has(item.id)
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => handleToggleItem(item)}
                                        className={cn(
                                            'w-full flex items-center gap-4 p-4 text-left transition-colors',
                                            'hover:bg-muted/50',
                                            isSelected && 'bg-primary/5'
                                        )}
                                    >
                                        {/* Checkbox */}
                                        <div
                                            className={cn(
                                                'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0',
                                                isSelected
                                                    ? 'bg-primary border-primary'
                                                    : 'border-muted-foreground'
                                            )}
                                        >
                                            {isSelected && (
                                                <svg
                                                    className="w-3 h-3 text-primary-foreground"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            )}
                                        </div>

                                        {/* Item Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium truncate">
                                                    {item.name}
                                                </span>
                                                <Badge
                                                    variant="outline"
                                                    className="text-xs"
                                                >
                                                    {item.item_type}
                                                </Badge>
                                            </div>
                                            <div className="text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 mt-1">
                                                {item.service_tag && (
                                                    <span>
                                                        ST: {item.service_tag}
                                                    </span>
                                                )}
                                                {item.serial_number && (
                                                    <span>
                                                        SN: {item.serial_number}
                                                    </span>
                                                )}
                                                <span>
                                                    Category: {item.category}
                                                </span>
                                                {item.current_location && (
                                                    <span>
                                                        @{' '}
                                                        {
                                                            item
                                                                .current_location
                                                                .name
                                                        }
                                                    </span>
                                                )}
                                                {item.rack_position_u && (
                                                    <span className="text-blue-600">
                                                        U{item.rack_position_u}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <Badge
                                            variant={getStatusBadgeVariant(
                                                item.status
                                            )}
                                        >
                                            {item.status}
                                        </Badge>
                                    </button>
                                )
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t">
                    <Label className="text-muted-foreground">
                        {selectedItems.size} item(s) selected
                    </Label>
                    <div className="flex gap-2">
                        <Button
                            variant="outlined"
                            type="button"
                            onClick={() => setOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            type="button"
                            onClick={handleAddSelected}
                            disabled={selectedItems.size === 0}
                        >
                            Add Selected
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default ItemSelector
